import React, { createContext, useCallback, useContext, useState } from "react";

export interface CartItem {
  id: number;
  name: string;
  price: number;
  salePrice: number | null;
  image: string;
}

interface CartContextValue {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: number) => void;
  clearCart: () => void;
  updateItemPrices: (updates: Array<{ id: number; price: number; salePrice: number | null }>) => void;
  itemCount: number;
  total: number;
}

const CartContext = createContext<CartContextValue | null>(null);

const STORAGE_KEY = "sosline-cart";

function isCartItem(value: unknown): value is CartItem {
  if (typeof value !== "object" || value === null) return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.id === "number" &&
    typeof item.name === "string" &&
    typeof item.price === "number" &&
    (item.salePrice === null || typeof item.salePrice === "number") &&
    typeof item.image === "string"
  );
}

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCartItem);
  } catch {
    // ignore parse errors
  }
  return [];
}

function saveCart(items: CartItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore storage errors
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => loadCart());

  const addItem = useCallback((item: CartItem) => {
    setItems((prev) => {
      if (prev.some((i) => i.id === item.id)) return prev;
      const next = [...prev, item];
      saveCart(next);
      return next;
    });
  }, []);

  const removeItem = useCallback((id: number) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.id !== id);
      saveCart(next);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    const next: CartItem[] = [];
    saveCart(next);
    setItems(next);
  }, []);

  const updateItemPrices = useCallback(
    (updates: Array<{ id: number; price: number; salePrice: number | null }>) => {
      setItems((prev) => {
        const next = prev.map((item) => {
          const update = updates.find((u) => u.id === item.id);
          if (!update) return item;
          return { ...item, price: update.price, salePrice: update.salePrice };
        });
        saveCart(next);
        return next;
      });
    },
    []
  );

  const itemCount = items.length;
  const total = items.reduce((sum, item) => sum + (item.salePrice ?? item.price), 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, clearCart, updateItemPrices, itemCount, total }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (ctx === null) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
}
